import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { Calendar, Camera, BookOpen, MessageCircle, Sparkles } from 'lucide-react';

const AppSection = () => {
  const sectionRef = useRef(null);
  const phoneRef = useRef(null);
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate visibility
      if (rect.top < windowHeight && rect.bottom > 0) {
        setIsVisible(true);
        
        // Calculate scroll progress through section
        const sectionTop = rect.top;
        const sectionHeight = rect.height;
        const progress = Math.max(0, Math.min(1, (windowHeight - sectionTop) / (windowHeight + sectionHeight)));
        setScrollProgress(progress);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate 3D rotation based on scroll
  const rotateY = -15 + (scrollProgress * 30); // -15 to 15 degrees
  const rotateX = 5 - (scrollProgress * 10); // 5 to -5 degrees
  const translateZ = scrollProgress * 50;
  const scale = 0.9 + (scrollProgress * 0.15);

  const features = [
    {
      icon: Calendar,
      title: language === 'de' ? 'Nachsorgeplanung' : 'Aftercare Planning',
      description: language === 'de' 
        ? 'Personalisierter Pflegeplan mit täglichen Erinnerungen und Meilensteinen für optimale Heilung.'
        : 'Personalized care plan with daily reminders and milestones for optimal healing.',
      color: 'from-[#7BA7C2] to-[#5A8BA6]'
    },
    {
      icon: Camera,
      title: language === 'de' ? 'Bilddokumentation' : 'Photo Documentation',
      description: language === 'de'
        ? 'Dokumentieren Sie Ihren Fortschritt mit der integrierten Kamera und vergleichen Sie Ergebnisse.'
        : 'Document your progress with the integrated camera and compare results.',
      color: 'from-[#5A8BA6] to-[#7BA7C2]'
    },
    {
      icon: BookOpen,
      title: language === 'de' ? 'Anleitungen' : 'Guides',
      description: language === 'de'
        ? 'Schritt-für-Schritt Videos und Guides für die perfekte Haarpflege nach der Behandlung.'
        : 'Step-by-step videos and guides for perfect hair care after treatment.',
      color: 'from-[#9BBFD9] to-[#7BA7C2]'
    },
    {
      icon: MessageCircle,
      title: language === 'de' ? 'Chat mit Experten' : 'Expert Chat',
      description: language === 'de'
        ? 'Direkter Kontakt zu unserem medizinischen Team für alle Ihre Fragen - 7 Tage die Woche.'
        : 'Direct contact with our medical team for all your questions - 7 days a week.',
      color: 'from-[#7BA7C2] to-[#9BBFD9]'
    }
  ];

  // App screens for the phone mockup
  const appScreens = [
    { day: language === 'de' ? 'Tag 1' : 'Day 1', task: language === 'de' ? 'Erste Haarwäsche' : 'First hair wash', status: 'completed' },
    { day: language === 'de' ? 'Tag 3' : 'Day 3', task: language === 'de' ? 'Kontrolle Spenderfläche' : 'Check donor area', status: 'completed' },
    { day: language === 'de' ? 'Tag 5' : 'Day 5', task: language === 'de' ? 'Medikamenteneinnahme' : 'Medication intake', status: 'completed' },
    { day: language === 'de' ? 'Tag 7' : 'Day 7', task: language === 'de' ? 'Krusten entfernen' : 'Remove crusts', status: 'active' },
    { day: language === 'de' ? 'Tag 10' : 'Day 10', task: language === 'de' ? 'Erste Sportaktivität' : 'First sports activity', status: 'upcoming' },
  ];

  return (
    <section 
      ref={sectionRef}
      className={`relative py-24 lg:py-32 overflow-hidden transition-colors duration-300 ${
        isDark ? 'bg-[#0f0f0f]' : 'bg-[#f8f9fa]'
      }`}
      style={{ perspective: '1500px' }}
    >
      {/* Background Effects */}
      <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-[#0a0a0a] via-[#0d1117] to-[#0a0a0a]' : 'bg-gradient-to-b from-white via-gray-50 to-white'}`}>
        <div className={`absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-[200px] animate-pulse ${
          isDark ? 'bg-[#7BA7C2]/10' : 'bg-[#5A8BA6]/5'
        }`} />
        <div className={`absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[80px] animate-pulse ${
          isDark ? 'bg-[#5A8BA6]/10' : 'bg-[#7BA7C2]/5'
        }`} style={{ animationDelay: '1s' }} />
        
        {/* Floating particles - static positions with gentle animation */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-1 h-1 rounded-full animate-pulse ${
              isDark ? 'bg-[#7BA7C2]/20' : 'bg-[#5A8BA6]/15'
            }`}
            style={{
              left: `${10 + (i * 4.5) % 80}%`,
              top: `${5 + (i * 7.3) % 90}%`,
              animationDuration: `${3 + (i % 4)}s`,
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-24">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-6 transition-all duration-700 ${
            isDark ? 'bg-[#7BA7C2]/10 border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 border-[#5A8BA6]/20'
          } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Sparkles className={`w-4 h-4 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`} />
            <span className={`text-sm font-medium ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
              {language === 'de' ? 'Neu: Dion Hair Clinic App' : 'New: Dion Hair Clinic App'}
            </span>
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-700 delay-100 ${
            isDark ? 'text-white' : 'text-gray-900'
          } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {language === 'de' ? 'Ihre Nachsorge.' : 'Your Aftercare.'}{' '}
            <span className="bg-gradient-to-r from-[#7BA7C2] via-[#9BBFD9] to-[#7BA7C2] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              Digital.
            </span>
          </h2>
          <p className={`max-w-2xl mx-auto text-lg transition-all duration-700 delay-200 ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          } ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {language === 'de' 
              ? 'Begleiten Sie Ihre Heilungsreise mit unserer exklusiven App - entwickelt für optimale Ergebnisse.'
              : 'Accompany your healing journey with our exclusive app - designed for optimal results.'}
          </p>
        </div>

        {/* Main Content - Phone + Features */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Left Side - Features */}
          <div className="order-2 lg:order-1 space-y-6">
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              const delay = 300 + (index * 100);
              return (
                <div
                  key={feature.title}
                  className={`group relative p-6 rounded-2xl border transition-all duration-500 ${
                    isDark 
                      ? 'bg-white/[0.02] border-white/5 hover:border-[#7BA7C2]/30 hover:bg-white/[0.04]'
                      : 'bg-white border-gray-200 hover:border-[#5A8BA6]/30'
                  } ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                  style={{ transitionDelay: `${delay}ms` }}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <FeatureIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-semibold mb-2 transition-colors duration-300 ${
                        isDark 
                          ? 'text-white group-hover:text-[#7BA7C2]'
                          : 'text-gray-900 group-hover:text-[#5A8BA6]'
                      }`}>
                        {feature.title}
                      </h3>
                      <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Hover glow effect */}
                  <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none ${
                    isDark ? 'bg-gradient-to-r from-[#7BA7C2]/0 via-[#7BA7C2]/5 to-[#7BA7C2]/0' : 'bg-gradient-to-r from-[#5A8BA6]/0 via-[#5A8BA6]/5 to-[#5A8BA6]/0'
                  }`} />
                </div>
              );
            })}
          </div>

          {/* Right Side - 3D Phone Mockup */}
          <div className="order-1 lg:order-2 flex justify-center items-center min-h-[500px] lg:min-h-[600px]">
            <div 
              ref={phoneRef}
              className="relative"
              style={{
                transform: `rotateY(${rotateY}deg) rotateX(${rotateX}deg) translateZ(${translateZ}px) scale(${scale})`,
                transformStyle: 'preserve-3d',
                transition: 'transform 0.1s ease-out'
              }}
            >
              {/* Phone Frame */}
              <div className={`relative w-[280px] sm:w-[300px] h-[580px] sm:h-[620px] rounded-[45px] p-[3px] shadow-2xl ${
                isDark 
                  ? 'bg-gradient-to-b from-[#2a2a2a] to-[#1a1a1a] shadow-black/50'
                  : 'bg-gradient-to-b from-[#e0e0e0] to-[#c0c0c0] shadow-gray-400/50'
              }`}>
                {/* Inner bezel */}
                <div className={`w-full h-full rounded-[42px] p-2 overflow-hidden ${
                  isDark ? 'bg-gradient-to-b from-[#1f1f1f] to-[#0f0f0f]' : 'bg-gradient-to-b from-[#f5f5f5] to-[#e8e8e8]'
                }`}>
                  {/* Notch */}
                  <div className={`absolute top-4 left-1/2 -translate-x-1/2 w-28 h-7 rounded-full z-20 ${
                    isDark ? 'bg-[#1a1a1a]' : 'bg-[#d0d0d0]'
                  }`} />
                  
                  {/* Screen */}
                  <div className={`w-full h-full rounded-[36px] overflow-hidden relative ${
                    isDark ? 'bg-gradient-to-b from-[#0a1520] to-[#050a10]' : 'bg-gradient-to-b from-[#f0f5f8] to-[#e8eef2]'
                  }`}>
                    {/* Status Bar */}
                    <div className="h-10 flex items-end justify-between px-6 pb-1">
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>9:41</span>
                      <div className="flex items-center gap-1">
                        <div className={`w-4 h-2 border rounded-sm ${isDark ? 'border-white/60' : 'border-gray-600'}`}>
                          <div className={`w-3/4 h-full rounded-sm ${isDark ? 'bg-white/60' : 'bg-gray-600'}`} />
                        </div>
                      </div>
                    </div>

                    {/* App Content - filled more */}
                    <div className="px-4 pt-2 pb-16 h-[calc(100%-40px)] flex flex-col">
                      {/* App Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                            {language === 'de' ? 'Willkommen zurück' : 'Welcome back'}
                          </p>
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {language === 'de' ? 'Ihre Nachsorge' : 'Your Aftercare'}
                          </h3>
                        </div>
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7BA7C2] to-[#5A8BA6] flex items-center justify-center">
                          <span className="text-white text-sm font-bold">DH</span>
                        </div>
                      </div>

                      {/* Progress Card */}
                      <div className={`rounded-xl p-3 mb-3 border ${
                        isDark 
                          ? 'bg-gradient-to-r from-[#7BA7C2]/20 to-[#5A8BA6]/20 border-[#7BA7C2]/30'
                          : 'bg-gradient-to-r from-[#5A8BA6]/10 to-[#7BA7C2]/10 border-[#5A8BA6]/20'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {language === 'de' ? 'Heilungsfortschritt' : 'Healing Progress'}
                          </span>
                          <span className={`text-xs font-bold ${isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'}`}>
                            {language === 'de' ? 'Tag 7' : 'Day 7'}
                          </span>
                        </div>
                        <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-black/30' : 'bg-gray-300'}`}>
                          <div 
                            className="h-full bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] rounded-full transition-all duration-1000"
                            style={{ width: `${Math.min(100, scrollProgress * 150)}%` }}
                          />
                        </div>
                        <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'de' ? '50% der kritischen Phase abgeschlossen' : '50% of critical phase completed'}
                        </p>
                      </div>

                      {/* Task List */}
                      <div className="space-y-2">
                        <p className={`text-[10px] uppercase tracking-wider mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'de' ? 'Nächste Schritte' : 'Next Steps'}
                        </p>
                        {appScreens.map((item, index) => (
                          <div 
                            key={index}
                            className={`flex items-center gap-2 p-2 rounded-lg transition-all duration-300 ${
                              item.status === 'active' 
                                ? isDark 
                                  ? 'bg-[#7BA7C2]/20 border border-[#7BA7C2]/40'
                                  : 'bg-[#5A8BA6]/15 border border-[#5A8BA6]/30'
                                : item.status === 'completed'
                                ? isDark ? 'bg-white/5' : 'bg-gray-100'
                                : isDark ? 'bg-white/[0.02]' : 'bg-gray-50'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                              item.status === 'completed' 
                                ? 'bg-green-500/20' 
                                : item.status === 'active'
                                ? isDark ? 'bg-[#7BA7C2]' : 'bg-[#5A8BA6]'
                                : isDark ? 'bg-white/10' : 'bg-gray-200'
                            }`}>
                              {item.status === 'completed' ? (
                                <svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <span className={`text-[10px] font-medium ${
                                  item.status === 'active' ? 'text-white' : isDark ? 'text-gray-500' : 'text-gray-400'
                                }`}>
                                  {item.day.replace('Tag ', '')}
                                </span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium truncate ${
                                item.status === 'active' 
                                  ? isDark ? 'text-white' : 'text-gray-900'
                                  : item.status === 'completed' 
                                  ? isDark ? 'text-gray-400' : 'text-gray-600'
                                  : isDark ? 'text-gray-500' : 'text-gray-400'
                              }`}>
                                {item.task}
                              </p>
                              <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{item.day}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bottom Navigation */}
                    <div className={`absolute bottom-0 left-0 right-0 h-14 ${
                      isDark ? 'bg-gradient-to-t from-[#0a1520] to-transparent' : 'bg-gradient-to-t from-[#f0f5f8] to-transparent'
                    }`}>
                      <div className="absolute bottom-2 left-4 right-4 flex justify-around items-center">
                        {[Calendar, Camera, MessageCircle, BookOpen].map((Icon, i) => (
                          <div 
                            key={i} 
                            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              i === 0 
                                ? isDark ? 'bg-[#7BA7C2]/20' : 'bg-[#5A8BA6]/15'
                                : ''
                            }`}
                          >
                            <Icon className={`w-4 h-4 ${
                              i === 0 
                                ? isDark ? 'text-[#7BA7C2]' : 'text-[#5A8BA6]'
                                : isDark ? 'text-gray-500' : 'text-gray-400'
                            }`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppSection;
